/**
 * useMembraneDynamicSchemaForm - Composable for managing dynamic form fields from JSON Schema
 *
 * Handles:
 * - Parsing JSON Schema into form field definitions
 * - Managing dynamic parameter values
 * - Form validation based on schema requirements
 */

import { DataSchema } from "@membranehq/sdk";
import { computed, ref, Ref } from "vue";

/**
 * Converts a field name to a prettier label
 * Examples:
 * - filterType -> Filter type
 * - reactionEmoji -> Reaction emoji
 * - conversationId -> Conversation id
 * - Workspace_id -> Workspace id
 * - user_name -> User name
 */
function prettifyFieldName(fieldName: string): string {
  // Replace underscores with spaces
  let words = fieldName.replace(/_/g, " ");

  // Split camelCase into words
  words = words.replace(/([A-Z])/g, " $1").trim();

  // Capitalize first letter, lowercase the rest
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
}

/**
 * Configuration describing a reference collection field, shared across the
 * Membrane dynamic schema/form composables and components.
 *
 * Parameter values are intentionally `unknown` because they come from an
 * untyped JSON Schema and can be static values, `"$field"` strings, or
 * `{ "$var": "field" }` objects.
 */
export interface ReferenceCollectionConfig {
  key: string;
  parameters?: Record<string, unknown>;
}

export interface DynamicField {
  key: string;
  label: string;
  type: string;
  required: boolean;
  description?: string;
  enum?: string[];
  referenceCollection?: ReferenceCollectionConfig;
  placeholder: string;
}

/** Normalizes a JSON Schema `type` (which may be an array) to a single string. */
function schemaTypeToString(type: DataSchema["type"] | null): string {
  return (typeof type === "string" ? type : undefined) || "string";
}

/** Builds a `ReferenceCollectionConfig` from a raw JSON Schema reference. */
function toReferenceCollectionConfig(
  referenceCollection: DataSchema["referenceCollection"]
): ReferenceCollectionConfig | undefined {
  if (!referenceCollection) return undefined;
  return {
    key: referenceCollection.key,
    parameters: referenceCollection.parameters,
  };
}

/** Builds a `DynamicField` from a single JSON Schema property. */
function buildDynamicField(
  key: string,
  schema: DataSchema,
  required: boolean
): DynamicField {
  return {
    key,
    label: schema.title || prettifyFieldName(key),
    type: schemaTypeToString(schema.type),
    required,
    description: schema.description,
    enum: schema.enum,
    referenceCollection: toReferenceCollectionConfig(
      schema.referenceCollection
    ),
    placeholder: `Enter ${schema.title || prettifyFieldName(key).toLowerCase()}`,
  };
}

export function useMembraneDynamicSchemaForm(
  parametersSchema: Ref<DataSchema | null>
) {
  /** Dynamic parameter values */
  const dynamicParameters = ref<Record<string, unknown>>({});

  /** Error message for invalid oneOf schemas */
  const oneOfError = ref<string | null>(null);

  /**
   * Analyzes the oneOf schema to extract discriminator field information
   *
   * Returns null if:
   * - No oneOf is present
   * - Branches don't all have the same discriminator field
   * - Discriminator field has different types across branches
   *
   * Sets oneOfError if schema is invalid.
   *
   * Returns discriminator info if valid:
   * - fieldName: The common discriminator field name
   * - values: Array of possible const values from each branch
   * - branches: Map of value -> branch schema
   */
  const oneOfDiscriminator = computed(() => {
    oneOfError.value = null;

    if (!parametersSchema.value?.oneOf) return null;

    const branches: DataSchema[] = parametersSchema.value.oneOf;
    if (!Array.isArray(branches) || branches.length === 0) return null;

    // Find a common discriminator field across all branches
    // It should have type: string and const: <value> in each branch
    const firstBranch = branches[0];
    const firstBranchProps: Record<string, DataSchema> =
      firstBranch.properties || {};

    let discriminatorField: string | null = null;
    let discriminatorType: DataSchema["type"] | null = null;
    let discriminatorDescription: string | undefined = undefined;

    // Look for a field with "const" in the first branch
    for (const [fieldName, fieldSchema] of Object.entries(firstBranchProps)) {
      if (fieldSchema.const !== undefined) {
        discriminatorField = fieldName;
        discriminatorType = fieldSchema.type;
        // For `oneOf` discriminator, we take the description from the first branch we see.
        // So it is wise to copy/paste the description in each branch as we don't know which one will be parsed first.
        discriminatorDescription = fieldSchema.description;
        break;
      }
    }

    if (!discriminatorField) {
      window.console.error(
        "oneOf branches found but no discriminator field with 'const' detected"
      );
      oneOfError.value =
        "Invalid data source configuration: cannot determine discriminator field. Please contact support.";
      return null;
    }

    // Verify all branches have the same discriminator field with the same type
    const values: string[] = [];
    const branchMap: Record<string, DataSchema> = {};

    for (const branch of branches) {
      const branchProps: Record<string, DataSchema> = branch.properties || {};
      const discriminatorSchema = branchProps[discriminatorField];

      if (!discriminatorSchema) {
        window.console.error(
          `oneOf branch missing discriminator field '${discriminatorField}'`
        );
        oneOfError.value =
          "Invalid data source configuration: inconsistent discriminator field. Please contact support.";
        return null;
      }

      if (discriminatorSchema.type !== discriminatorType) {
        window.console.error(
          `oneOf discriminator field '${discriminatorField}' has inconsistent types across branches`
        );
        oneOfError.value =
          "Invalid data source configuration: discriminator field has inconsistent types. Please contact support.";
        return null;
      }

      if (discriminatorSchema.const === undefined) {
        window.console.error(
          `oneOf discriminator field '${discriminatorField}' missing 'const' value in branch`
        );
        oneOfError.value =
          "Invalid data source configuration: discriminator field missing value. Please contact support.";
        return null;
      }

      const constValue = discriminatorSchema.const;
      values.push(constValue);
      branchMap[constValue] = branch;
    }

    return {
      fieldName: discriminatorField,
      type: discriminatorType,
      description: discriminatorDescription,
      values,
      branches: branchMap,
    };
  });

  /**
   * Gets the currently selected oneOf branch based on discriminator value
   */
  const oneOfSelectedBranch = computed(() => {
    const discriminator = oneOfDiscriminator.value;
    if (!discriminator) return null;

    const selectedValue = dynamicParameters.value[discriminator.fieldName];
    if (!selectedValue || typeof selectedValue !== "string") return null;

    return discriminator.branches[selectedValue] || null;
  });

  /**
   * Gets the fields for the currently selected oneOf branch
   * (excluding the discriminator field itself)
   */
  const oneOfBranchFields = computed<DynamicField[]>(() => {
    const branch = oneOfSelectedBranch.value;
    const discriminator = oneOfDiscriminator.value;
    if (!branch || !discriminator) return [];

    const branchProps: Record<string, DataSchema> = branch.properties || {};
    const branchRequired = branch.required || [];

    return Object.entries(branchProps)
      .filter(([key]) => key !== discriminator.fieldName) // Exclude discriminator
      .map(([key, schema]) =>
        buildDynamicField(key, schema, branchRequired.includes(key))
      );
  });

  /**
   * Computes form fields from the JSON Schema
   * Handles both regular properties and oneOf discriminators
   */
  const dynamicFields = computed<DynamicField[]>(() => {
    if (!parametersSchema.value) return [];

    const fields: DynamicField[] = [];

    // 1. Add root properties first
    const rootProps: Record<string, DataSchema> =
      parametersSchema.value.properties || {};
    const rootRequired = parametersSchema.value.required || [];

    for (const [key, schema] of Object.entries(rootProps)) {
      fields.push(buildDynamicField(key, schema, rootRequired.includes(key)));
    }

    // 2. Add discriminator field if oneOf exists
    const discriminator = oneOfDiscriminator.value;
    if (discriminator) {
      // Check if discriminator is required in all branches
      const allBranches: DataSchema[] = parametersSchema.value.oneOf || [];
      const isRequired = allBranches.every((branch) =>
        (branch.required || []).includes(discriminator.fieldName)
      );

      fields.push({
        key: discriminator.fieldName,
        label: prettifyFieldName(discriminator.fieldName),
        type: schemaTypeToString(discriminator.type),
        required: isRequired,
        description: discriminator.description,
        enum: discriminator.values,
        referenceCollection: undefined,
        placeholder: `Select ${prettifyFieldName(discriminator.fieldName).toLowerCase()}`,
      });
    }

    // 3. Add branch-specific fields
    fields.push(...oneOfBranchFields.value);

    return fields;
  });

  /**
   * Validates that all required dynamic parameters are filled
   */
  const areDynamicParametersValid = computed(() => {
    for (const field of dynamicFields.value) {
      if (field.required && !dynamicParameters.value[field.key]) {
        return false;
      }
    }
    return true;
  });

  /**
   * Initializes dynamic parameters with empty strings
   */
  const initializeDynamicParameters = () => {
    dynamicParameters.value = {};
    if (parametersSchema.value?.properties) {
      Object.keys(parametersSchema.value.properties).forEach((key) => {
        dynamicParameters.value[key] = "";
      });
    }

    // Also initialize discriminator field if oneOf exists
    const discriminator = oneOfDiscriminator.value;
    if (discriminator) {
      dynamicParameters.value[discriminator.fieldName] = "";
    }
  };

  /**
   * Resets all dynamic parameters
   */
  const resetDynamicParameters = () => {
    dynamicParameters.value = {};
  };

  /**
   * Clears parameters for branch-specific fields (used when discriminator changes)
   */
  const clearBranchParameters = () => {
    const branchFields = oneOfBranchFields.value;
    for (const field of branchFields) {
      delete dynamicParameters.value[field.key];
    }
  };

  return {
    dynamicParameters,
    dynamicFields,
    areDynamicParametersValid,
    initializeDynamicParameters,
    resetDynamicParameters,
    oneOfDiscriminator,
    oneOfSelectedBranch,
    oneOfBranchFields,
    oneOfError,
    clearBranchParameters,
  };
}
